import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { fieldSeasonApi, warehouseSeasonApi } from '../services/api';

const SeasonContext = createContext();

export const useSeasonContext = () => {
  const context = useContext(SeasonContext);
  if (!context) {
    throw new Error('useSeasonContext must be used within a SeasonProvider');
  }
  return context;
};

// Funkcje pomocnicze do zarządzania localStorage
const getStoredSeason = () => {
  try {
    const stored = localStorage.getItem('selectedSeason');
    return stored ? parseInt(stored, 10) : null;
  } catch (error) {
    console.warn('Error reading from localStorage:', error);
    return null;
  }
};

const storeSelectedSeason = (season) => {
  try {
    localStorage.setItem('selectedSeason', season.toString());
  } catch (error) {
    console.warn('Error writing to localStorage:', error);
  }
};

export const SeasonProvider = ({ children }) => {
  // Inicjalizacja currentSeason z localStorage lub aktualnego roku
  const [currentSeason, setCurrentSeason] = useState(() => {
    return getStoredSeason() || new Date().getFullYear();
  });
  const [availableSeasons, setAvailableSeasons] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAvailableSeasons = useCallback(async () => {
    try {
      setLoading(true);
      // Fetch seasons from both field seasons and warehouse seasons
      const [fieldSeasonsResponse, warehouseSeasonsResponse] = await Promise.all([
        fieldSeasonApi.getAvailableYears().catch(() => ({ data: [] })),
        warehouseSeasonApi.getAvailableYears().catch(() => ({ data: [] }))
      ]);

      // Combine and deduplicate seasons from both sources
      const allSeasons = [...new Set([
        ...fieldSeasonsResponse.data,
        ...warehouseSeasonsResponse.data
      ])].sort((a, b) => b - a); // Sort descending (newest first)

      setAvailableSeasons(allSeasons);

      // Jeśli aktualny rok nie jest w dostępnych sezonach, ustaw pierwszy dostępny.
      // Funkcyjna forma setState pozwala uniknąć zależności od currentSeason,
      // dzięki czemu lista sezonów nie jest pobierana ponownie przy każdej zmianie sezonu.
      setCurrentSeason((prev) =>
        allSeasons.length > 0 && !allSeasons.includes(prev) ? allSeasons[0] : prev
      );
    } catch (err) {
      console.error('Error fetching available seasons:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAvailableSeasons();
  }, [fetchAvailableSeasons]);

  const changeSeason = useCallback((season) => {
    const newSeason = parseInt(season, 10);
    if (Number.isNaN(newSeason)) return;
    setCurrentSeason(newSeason);
    storeSelectedSeason(newSeason);
  }, []);

  const refreshSeasons = useCallback(() => {
    fetchAvailableSeasons();
  }, [fetchAvailableSeasons]);

  const createSeason = useCallback(async (year) => {
    try {
      // Initialize warehouse seasons from templates for the new year
      await warehouseSeasonApi.initializeFromTemplates(year);

      // Refresh available seasons from the backend
      await fetchAvailableSeasons();

      // Set the current season to the newly created year and store it
      setCurrentSeason(year);
      storeSelectedSeason(year);
    } catch (error) {
      console.error('Error creating season:', error);
      throw error; // Re-throw to be handled by the component
    }
  }, [fetchAvailableSeasons]);

  const value = useMemo(() => ({
    currentSeason,
    availableSeasons,
    loading,
    changeSeason,
    refreshSeasons,
    createSeason
  }), [currentSeason, availableSeasons, loading, changeSeason, refreshSeasons, createSeason]);

  return (
    <SeasonContext.Provider value={value}>
      {children}
    </SeasonContext.Provider>
  );
};